import { createSelector, createSelectorCreator, weakMapMemoize } from "@reduxjs/toolkit";

import { sortByTermYearRegex } from "./sorting";

import { PublicationType, TermType } from "components/server-types.ts";
import {
  DEFAULT_ORDER_FN,
  ORDER_BY_CODE,
  ORDER_BY_CREATED,
  ORDER_BY_CREATED_DATE_DESC,
  ORDER_BY_NAME,
  ORDER_BY_TITLE,
} from "mds/hooks/use-ordered";
import { RootState } from "store";
import { areShallowRecordsEqual, areStringArraysEqual } from "store/selector-memoize";
import { filterPageAccessDataByInteractionMode } from "utils/page-utils";
import {
  CourseScopedModelType,
  ModelType,
  PageScopedModelType,
  PrincipalOwnedModelType,
  SectionScopedModelType,
  UserScopedModelType,
} from "utils/type-utils";

export const createAppSelector = createSelector.withTypes<RootState>();

/** Like createAppSelector, with content equality for `string[]` results. */
export const createStringArraySelector = createSelectorCreator({
  memoize: weakMapMemoize,
  memoizeOptions: { resultEqualityCheck: areStringArraysEqual },
}).withTypes<RootState>();

/** Like createAppSelector, with shallow equality for flat record results. */
export const createShallowRecordSelector = createSelectorCreator({
  memoize: weakMapMemoize,
  memoizeOptions: { resultEqualityCheck: areShallowRecordsEqual },
}).withTypes<RootState>();

export const selectCurrentUserId = (state: RootState) => state.auth.user_id;
export const selectUserJWT = (state: RootState) => state.auth.collaboration_jwt;

// This needs to be in base.ts instead of view.ts to avoid circular dependencies
export const selectCurrentSectionId = (state: RootState) => state.view.currentSectionId;
export const selectCurrentPageId = (state: RootState) => state.view.currentPageId;

// Unified set of selectors for all slices

type SliceSelector<T> = (state: RootState) => Record<string, T>;
type SelectorOptionsType<T> = {
  sortingFunction?: (a: T, b: T) => number;
};

export const createSelectEntities = <T>(getSlice: SliceSelector<T>) => getSlice;

const createSelectById =
  <T>(getSlice: SliceSelector<T>) =>
  (state: RootState, id: string | null | undefined): T | null => {
    const slice = getSlice(state);
    return id && id in slice ? slice[id] : null;
  };

const createSelectAll = <T>(getSlice: SliceSelector<T>, options?: SelectorOptionsType<T>) =>
  createAppSelector([createSelectEntities(getSlice)], (slice) => {
    const values = Object.values(slice);
    if (options?.sortingFunction) values.sort(options.sortingFunction);
    return values;
  });

const createSelectIds = <T extends { id: string }>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) =>
  createStringArraySelector([createSelectAll(getSlice, options)], (allObjects) =>
    allObjects.map((item) => item.id),
  );

const createSelectUserIds = <T extends { user_id: string }>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) =>
  createStringArraySelector([createSelectAll(getSlice, options)], (allObjects) =>
    allObjects.map((item) => item.user_id),
  );

const createFilterSelector = <T>(
  getSlice: SliceSelector<T>,
  attribute: keyof T,
  options?: SelectorOptionsType<T>,
) =>
  createAppSelector(
    [
      createSelectAll(getSlice, options),
      (_state: RootState, value: T[keyof T] | null | undefined) => value,
    ],
    (allObjects, value) => allObjects.filter((item) => item[attribute] === value),
  );

const createFilterForCurrentSectionSelector = <T extends { section_id: string }>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) =>
  createAppSelector(
    [createSelectAll(getSlice, options), selectCurrentSectionId],
    (allObjects, currentSectionId) =>
      currentSectionId ? allObjects.filter((item) => item.section_id === currentSectionId) : [],
  );

const createFilterForCurrentUserSelector = <T extends { user_id: string }>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) =>
  createAppSelector(
    [createSelectAll(getSlice, options), selectCurrentUserId],
    (allObjects, currentUserId) => allObjects.filter((item) => item.user_id === currentUserId),
  );

const createFilterForCurrentCourseSelector = <T extends { course_id: string }>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) =>
  createAppSelector(
    [
      createSelectAll(getSlice, options),
      selectCurrentSectionId,
      (state: RootState) => state.sections,
    ],
    (allObjects, currentSectionId, sections) => {
      const courseId = currentSectionId != null ? sections[currentSectionId]?.course_id : undefined;
      return courseId ? allObjects.filter((item) => item.course_id === courseId) : [];
    },
  );

// Base selectors for all slices/models
const createBaseSelectors = <T extends ModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    selectById: createSelectById(getSlice),
    selectAll: createSelectAll(getSlice, options),
    selectEntities: createSelectEntities(getSlice),
    selectIds: createSelectIds(getSlice, options),
  };
};

// Selectors for slices/models that are scoped to a section
const createSectionScopedSelectors = <T extends SectionScopedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createBaseSelectors(getSlice, options),
    filterBySectionId: createFilterSelector<T>(getSlice, "section_id", options),
    filterForCurrentSection: createFilterForCurrentSectionSelector<T>(getSlice, options),
  };
};

// Selectors for slices/models that are scoped to a user
const createUserScopedSelectors = <T extends UserScopedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createBaseSelectors(getSlice, options),
    selectUserIds: createSelectUserIds(getSlice, options),
    filterByUserId: createFilterSelector<T>(getSlice, "user_id", options),
    filterForCurrentUser: createFilterForCurrentUserSelector<T>(getSlice, options),
  };
};

const createCourseScopedSelectors = <T extends CourseScopedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createBaseSelectors(getSlice, options),
    filterByCourseId: createFilterSelector<T>(getSlice, "course_id", options),
    filterForCurrentCourse: createFilterForCurrentCourseSelector<T>(getSlice, options),
  };
};

// Selectors for slices/models that are scoped to a page
const createPageScopedSelectors = <T extends PageScopedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createSectionScopedSelectors(getSlice, options),
    filterByPageId: createFilterSelector<T>(getSlice, "page_id", options),
  };
};

// Selectors for principal-owned slices/models
const createPrincipalOwnedPageScopedSelectors = <T extends PrincipalOwnedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createPageScopedSelectors(getSlice, options),
    filterBySectionUserId: createFilterSelector<T>(getSlice, "section_user_id", options),
    filterByPageGroupId: createFilterSelector<T>(getSlice, "page_group_id", options),
  };
};

export const activitySelectors = createSectionScopedSelectors((s) => s.activities);
export const assessmentSelectors = createSectionScopedSelectors((s) => s.assessments);
export const breakoutGroupSelectors = createSectionScopedSelectors((s) => s.breakout_groups);
export const chatMessageSelectors = createSectionScopedSelectors((s) => s.chat_messages, {
  sortingFunction: ORDER_BY_CREATED_DATE_DESC,
});
export const classSessionSelectors = createSectionScopedSelectors((s) => s.class_sessions);
export const classSessionSummarySelectors = createSectionScopedSelectors(
  (s) => s.class_session_summaries,
);
export const classSessionUserSummarySelectors = {
  ...createSectionScopedSelectors((s) => s.class_session_user_summaries),
  ...createUserScopedSelectors((s) => s.class_session_user_summaries),
};
export const classUserSelectors = createSectionScopedSelectors((s) => s.class_users);
export const courseGroupingSelectors = createBaseSelectors((s) => s.course_groupings, {
  sortingFunction: ORDER_BY_TITLE,
});
export const courseSelectors = createBaseSelectors((s) => s.courses, {
  sortingFunction: ORDER_BY_CODE,
});

export const courseUserSelectors = {
  ...createUserScopedSelectors((s) => s.course_users),
  ...createCourseScopedSelectors((s) => s.course_users),
};
export const courseGroupingUserSelectors = createUserScopedSelectors(
  (s) => s.course_grouping_users,
);
export const orgUserSelectors = createUserScopedSelectors((s) => s.org_users);
export const orgSelectors = createBaseSelectors((s) => s.orgs, {
  sortingFunction: ORDER_BY_NAME,
});
export const outcomeAssessmentSelectors = createBaseSelectors((s) => s.outcome_assessments);
export const outcomeGroupSelectors = createBaseSelectors((s) => s.outcome_groups, {
  sortingFunction: ORDER_BY_TITLE,
});
export const outcomeSubgroupSelectors = createBaseSelectors((s) => s.outcome_subgroups, {
  sortingFunction: ORDER_BY_TITLE,
});
export const outcomeSelectors = createBaseSelectors((s) => s.outcomes, {
  sortingFunction: ORDER_BY_TITLE,
});
const basePageAccessDataSelectors = createPrincipalOwnedPageScopedSelectors(
  (s) => s.page_access_data,
);
export const pageGroupUserSelectors = createSectionScopedSelectors((s) => s.page_group_users);
// Keep historical records in Redux, but exclude archived groups
// from every normal group selector (cards, summaries, pickers and breakouts).
const selectActivePageGroupEntities = createAppSelector([(state) => state.page_groups], (groups) =>
  Object.fromEntries(Object.entries(groups).filter(([, group]) => !group.is_archived)),
);
export const pageGroupSelectors = createPageScopedSelectors(selectActivePageGroupEntities, {
  sortingFunction: ORDER_BY_CREATED,
});
export const pageOutcomeSelectors = createPageScopedSelectors((s) => s.page_outcomes);
export const pageSelectors = createSectionScopedSelectors((s) => s.pages, {
  sortingFunction: DEFAULT_ORDER_FN,
});
export const pageAccessDataSelectors = {
  ...basePageAccessDataSelectors,
  filterByPageId: createAppSelector(
    [
      basePageAccessDataSelectors.filterByPageId,
      pageSelectors.selectById,
      (state) => state.page_groups,
      (_state: RootState, pageId: string | null | undefined) => pageId,
    ],
    (pageAccessData, page, groups) =>
      filterPageAccessDataByInteractionMode(page, pageAccessData).filter(
        (data) => !data.page_group_id || !groups[data.page_group_id]?.is_archived,
      ),
  ),
};
export const publicationSelectors = createSectionScopedSelectors((s) => s.publications, {
  sortingFunction: (a: PublicationType, b: PublicationType) => b.version - a.version,
});
export const recordingSessionSelectors = createSectionScopedSelectors((s) => s.recording_sessions);
export const sectionOutcomeSelectors = createSectionScopedSelectors((s) => s.section_outcomes);
export const sectionSelectors = createBaseSelectors((s) => s.sections, {
  sortingFunction: ORDER_BY_TITLE,
});
export const sectionUserSelectors = {
  ...createSectionScopedSelectors((s) => s.section_users),
  ...createUserScopedSelectors((s) => s.section_users),
};
export const submissionSelectors = createPrincipalOwnedPageScopedSelectors((s) => s.submissions);
export const termSelectors = createBaseSelectors<TermType>((s) => s.terms, {
  sortingFunction: sortByTermYearRegex,
});
export const topicSelectors = createSectionScopedSelectors((s) => s.topics);
export const userSelectors = createBaseSelectors((s) => s.users);

export const selectBulkImportJobById = createSelectById((state) => state.bulk_import_jobs);
